import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Linkedin } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface PublicParticipant {
  id: string;
  event_id: string;
  first_name: string;
  last_name: string;
  company: string;
  photo_url: string | null;
  linkedin_url: string | null;
}

export const participantPhotoUrl = (id: string) =>
  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/participant-photo?id=${id}`;

export const initialsOf = (first: string, last: string) =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

export const useParticipants = (eventId?: string) => {
  const [participants, setParticipants] = useState<PublicParticipant[] | null>(null);

  useEffect(() => {
    if (!eventId) {
      setParticipants(null);
      return;
    }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from("event_participants_public")
        .select("id, event_id, first_name, last_name, company, photo_url, linkedin_url")
        .eq("event_id", eventId);
      if (active) setParticipants((data || []) as PublicParticipant[]);
    })();
    return () => {
      active = false;
    };
  }, [eventId]);

  return participants;
};

export function ParticipantAvatar({ participant }: { participant: PublicParticipant }) {
  const { first_name, last_name, company, photo_url, linkedin_url } = participant;
  const photo = photo_url ? participantPhotoUrl(participant.id) : null;
  const name = `${first_name} ${last_name}`;
  const label = `${name}, ${company}`;

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-14 w-14 rounded-full p-0 ring-offset-background hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label={label}
    >
      <Avatar className="h-14 w-14 border-2 border-background shadow-sm ring-1 ring-border">
        {photo && (
          <AvatarImage
            src={photo}
            alt=""
            loading="lazy"
            className="object-cover"
          />
        )}
        <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
          {initialsOf(first_name, last_name)}
        </AvatarFallback>
      </Avatar>
    </Button>
  );

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="hidden text-center md:block">
          <p className="font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">{company}</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-64 p-3" align="center">
        <p className="font-medium text-foreground">{name}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{company}</p>
        {linkedin_url && (
          <Button asChild variant="outline" size="sm" className="mt-3">
            <a
              href={linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Öppna ${name} på LinkedIn`}
            >
              <Linkedin className="mr-1.5 h-4 w-4" />
              LinkedIn
            </a>
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function ParticipantPreview({ participants }: { participants: PublicParticipant[] | null }) {
  if (!participants) return null;

  if (participants.length === 0) {
    return (
      <section className="mb-8 rounded-md border border-border bg-muted/30 p-5">
        <h2 className="text-xl font-semibold text-foreground">Några som planerar att vara med</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Vill du visa andra att du planerar att delta? Du är välkommen att synas här först.
        </p>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <a href="#visa-deltagande">Visa att du planerar att delta</a>
        </Button>
      </section>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <section className="mb-8" aria-labelledby="participant-preview-heading">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <h2 id="participant-preview-heading" className="text-xl font-semibold text-foreground">
            Några som planerar att vara med
          </h2>
          <a href="#natverkande" className="text-sm font-medium text-primary hover:underline">
            Se alla som planerar 
          </a>
        </div>
        <div className="flex flex-wrap gap-2" role="list" aria-label="Deltagare">
          {participants.slice(0, 8).map((participant) => (
            <div key={participant.id} role="listitem">
              <ParticipantAvatar participant={participant} />
            </div>
          ))}
        </div>
        <Button asChild variant="outline" size="sm" className="mt-4">
          <a href="#visa-deltagande">Visa att du planerar att delta</a>
        </Button>
      </section>
    </TooltipProvider>
  );
}

export default function ParticipantList({ participants }: { participants: PublicParticipant[] | null }) {
  if (!participants || participants.length === 0) return null;

  return (
    <section id="natverkande" className="mb-8 scroll-mt-28" aria-labelledby="networking-heading">
      <h2 id="networking-heading" className="mb-2 text-xl font-semibold text-foreground">
        Nätverkande
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Lär känna några av dem som planerar att vara med och fortsätt gärna samtalet efter träffen.
      </p>
      <div className="divide-y divide-border rounded-md border border-border bg-card">
        {participants.map((participant) => (
          <div key={participant.id} className="flex items-center gap-3 px-3 py-3">
            <Avatar className="h-11 w-11 border border-border">
              {participant.photo_url && (
                <AvatarImage
                  src={participantPhotoUrl(participant.id)}
                  alt=""
                  loading="lazy"
                  className="object-cover"
                />
              )}
              <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
                {initialsOf(participant.first_name, participant.last_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">
                {participant.first_name} {participant.last_name}
              </p>
              <p className="truncate text-xs text-muted-foreground">{participant.company}</p>
            </div>
            {participant.linkedin_url && (
              <Button asChild variant="ghost" size="sm" className="shrink-0">
                <a
                  href={participant.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Öppna ${participant.first_name} ${participant.last_name} på LinkedIn`}
                >
                  <Linkedin className="mr-1.5 h-4 w-4" />
                  <span className="hidden sm:inline">LinkedIn</span>
                </a>
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
